/**
 * Gmail Connection Component
 *
 * Allows users to connect their Gmail account for receiving DTEs via IMAP.
 * Supports OAuth2 authentication for Gmail/Google Workspace accounts.
 */

import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Mail, Check, AlertCircle, Loader2, ExternalLink } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useGmailConnection } from "../hooks/useGmailConnection";

export function GmailConnection() {
  const {
    status,
    isLoading,
    isConnecting,
    isDisconnecting,
    connect,
    disconnect,
    oauthError,
  } = useGmailConnection();

  const [email, setEmail] = useState("");
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);

  // Show OAuth error toast
  useEffect(() => {
    if (oauthError) {
      const errorMessages: Record<string, string> = {
        access_denied:
          "Acceso denegado. Por favor, autoriza el acceso a Gmail.",
        missing_parameters:
          "Error en la respuesta de Google. Intenta nuevamente.",
      };
      toast.error(errorMessages[oauthError] || `Error de OAuth: ${oauthError}`);
    }
  }, [oauthError]);

  const handleConnect = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Ingresa una direccion de correo valida");
      return;
    }

    try {
      await connect(email);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Error al conectar Gmail",
      );
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect();
      setShowDisconnectDialog(false);
      toast.success("Gmail desconectado exitosamente");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Error al desconectar Gmail",
      );
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Conexion Gmail
          </CardTitle>
          <CardDescription>
            Conecta tu cuenta de Gmail para recibir automaticamente los DTEs
            enviados por el SII y tus proveedores.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {status.connected ? (
            // Connected state
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                <Check className="h-5 w-5 text-green-600" />
                <div className="flex-1">
                  <p className="font-medium text-green-900">Gmail conectado</p>
                  <p className="text-sm text-green-700">{status.email}</p>
                </div>
              </div>

              <div className="text-sm text-muted-foreground">
                <p>
                  Los correos con DTEs seran procesados automaticamente y
                  apareceran en la seccion de Documentos Recibidos.
                </p>
              </div>

              <Button
                variant="outline"
                onClick={() => setShowDisconnectDialog(true)}
                disabled={isDisconnecting}
              >
                {isDisconnecting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Desconectando...
                  </>
                ) : (
                  "Desconectar Gmail"
                )}
              </Button>
            </div>
          ) : (
            // Not connected state
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-900">
                    Gmail no conectado
                  </p>
                  <p className="text-sm text-amber-700">
                    Conecta tu cuenta de Gmail para recibir DTEs
                    automaticamente.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="gmail-email">Correo electronico</Label>
                  <Input
                    id="gmail-email"
                    type="email"
                    placeholder="empresa@ejemplo.cl"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isConnecting}
                  />
                  <p className="text-xs text-muted-foreground">
                    Ingresa la direccion de Gmail donde recibes los DTEs del
                    SII.
                  </p>
                </div>

                <Button
                  onClick={handleConnect}
                  disabled={isConnecting || !email}
                  className="gap-2"
                >
                  {isConnecting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Conectando...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4" />
                      Conectar Gmail
                    </>
                  )}
                </Button>
              </div>

              <div className="text-xs text-muted-foreground space-y-2 pt-4 border-t">
                <p>
                  Al conectar, seras redirigido a Google para autorizar el
                  acceso de lectura a tu correo. Solo leemos correos
                  relacionados con DTEs.
                </p>
                <p className="flex items-center gap-1">
                  <ExternalLink className="h-3 w-3" />
                  <a
                    href="https://myaccount.google.com/permissions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Administrar permisos de aplicaciones en Google
                  </a>
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Disconnect confirmation dialog */}
      <AlertDialog
        open={showDisconnectDialog}
        onOpenChange={setShowDisconnectDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desconectar Gmail</AlertDialogTitle>
            <AlertDialogDescription>
              Si desconectas Gmail, ya no se procesaran automaticamente los DTEs
              recibidos por correo. Los documentos ya procesados se mantendran
              en el sistema.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisconnect}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Desconectar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
